import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { ComplianceManagement } from './ComplianceManagement';
import { SupportInboxTab } from './SupportInboxTab';

interface TenantRole {
  tenant_id: string;
  tenant_name: string;
  role: 'superuser' | 'owner' | 'admin' | 'user';
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'inactive' | 'suspended';
  role: 'superuser' | 'owner' | 'admin' | 'user';
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  tenant_roles?: TenantRole[];
  is_owner_anywhere?: boolean;
}

interface Tenant {
  id: string;
  name: string;
  domain?: string;
  status: 'active' | 'suspended' | 'cancelled';
  created_at: string;
  updated_at: string;
}

interface ServerStats {
  total_users: number;
  total_tenants: number;
  active_users: number;
  active_tenants: number;
  recent_registrations: number;
}

export const ServerAdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'tenants' | 'compliance' | 'diagnostics' | 'support-inbox'>('overview');
  const [searchTerm, setSearchTerm] = useState('');

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedUsers(new Set());
    setSelectedTenants(new Set());
  }, [activeTab]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [verificationBypassEnabled, setVerificationBypassEnabled] = useState<boolean>(false);
  const [isUpdatingBypass, setIsUpdatingBypass] = useState(false);
  const [campaignSettingsLockEnabled, setCampaignSettingsLockEnabled] = useState<boolean>(false);
  const [isUpdatingCampaignLock, setIsUpdatingCampaignLock] = useState(false);
  const [ratingGateModeLockEnabled, setRatingGateModeLockEnabled] = useState<boolean>(false);
  const [isUpdatingRatingGateLock, setIsUpdatingRatingGateLock] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'user' | 'tenant' | null; id: string | null; name: string | null }>({ type: null, id: null, name: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailDiagnostics, setEmailDiagnostics] = useState<any>(null);
  const [isLoadingDiagnostics, setIsLoadingDiagnostics] = useState(false);
  const [selectedTenantForDiagnostics, setSelectedTenantForDiagnostics] = useState<string>('');
  const [showAssignTenantModal, setShowAssignTenantModal] = useState(false);
  const [userToAssign, setUserToAssign] = useState<User | null>(null);
  const [selectedTenantForAssignment, setSelectedTenantForAssignment] = useState<string>('');
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState<'user' | 'admin' | 'owner' | 'superuser'>('user');
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadVerificationBypassSetting();
    loadCampaignSettingsLockSetting();
    loadRatingGateModeLockSetting();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load server statistics
      try {
        const statsResponse = await api.serverAdmin.getStats();
        setStats(statsResponse.data);
      } catch (error) {
        console.warn('Failed to load server stats:', error);
      }

      // Load users
      try {
        const usersResponse = await api.serverAdmin.getUsers();
        setUsers(usersResponse.data);
      } catch (error) {
        console.warn('Failed to load users:', error);
      }

      // Load tenants
      try {
        const tenantsResponse = await api.serverAdmin.getTenants();
        setTenants(tenantsResponse.data);
      } catch (error) {
        console.warn('Failed to load tenants:', error);
      }

    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'error', text: 'Failed to load server data' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserStatusChange = async (userId: string, newStatus: 'active' | 'inactive' | 'suspended') => {
    try {
      await api.serverAdmin.updateUserStatus(userId, { status: newStatus });
      
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, status: newStatus } : user
      ));
      
      setMessage({ type: 'success', text: `User status updated to ${newStatus}` });
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update user status' 
      });
    }
  };

  const handleUserRoleChange = async (userId: string, newRole: 'superuser' | 'owner' | 'admin' | 'user') => {
    try {
      await api.serverAdmin.updateUserRole(userId, { role: newRole });
      
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      setMessage({ type: 'success', text: `User role updated to ${newRole}` });
    } catch (error: any) {
      console.error('Failed to update user role:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update user role' 
      });
    }
  };

  const handleTenantRoleChange = async (userId: string, tenantId: string, newRole: 'superuser' | 'owner' | 'admin' | 'user') => {
    try {
      await api.serverAdmin.updateUserTenantRole(userId, tenantId, { role: newRole });
      
      // Reload users to get updated tenant roles
      const usersResponse = await api.serverAdmin.getUsers();
      setUsers(usersResponse.data);
      
      setMessage({ type: 'success', text: `Tenant role updated to ${newRole}` });
    } catch (error: any) {
      console.error('Failed to update tenant role:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update tenant role' 
      });
    }
  };

  const handleAssignTenantClick = (user: User) => {
    setUserToAssign(user);
    setSelectedTenantForAssignment('');
    setSelectedRoleForAssignment('user');
    setShowAssignTenantModal(true);
  };

  const handleAssignTenant = async () => {
    if (!userToAssign || !selectedTenantForAssignment) {
      setMessage({ type: 'error', text: 'Please select a tenant' });
      return;
    }

    try {
      setIsAssigning(true);
      await api.serverAdmin.assignUserToTenant(userToAssign.id, {
        tenant_id: selectedTenantForAssignment,
        role: selectedRoleForAssignment
      });
      
      // Reload users to get updated tenant roles
      const usersResponse = await api.serverAdmin.getUsers();
      setUsers(usersResponse.data);
      
      setMessage({ type: 'success', text: `User assigned to tenant successfully` });
      setShowAssignTenantModal(false);
      setUserToAssign(null);
      setSelectedTenantForAssignment('');
    } catch (error: any) {
      console.error('Failed to assign tenant:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to assign tenant' 
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setIsDeleting(true);
      await api.serverAdmin.deleteUser(userId);
      
      setUsers(prev => prev.filter(user => user.id !== userId));
      setDeleteConfirm({ type: null, id: null, name: null });
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      setMessage({ type: 'success', text: 'User deleted successfully' });
      
      // Reload stats to update counts
      const statsResponse = await api.serverAdmin.getStats();
      setStats(statsResponse.data);
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete user' 
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    try {
      setIsDeleting(true);
      await api.serverAdmin.deleteTenant(tenantId);
      
      setTenants(prev => prev.filter(tenant => tenant.id !== tenantId));
      setDeleteConfirm({ type: null, id: null, name: null });
      setSelectedTenants(prev => {
        const newSet = new Set(prev);
        newSet.delete(tenantId);
        return newSet;
      });
      setMessage({ type: 'success', text: 'Tenant and all associated data deleted successfully' });
      
      // Reload stats to update counts
      const statsResponse = await api.serverAdmin.getStats();
      setStats(statsResponse.data);
    } catch (error: any) {
      console.error('Failed to delete tenant:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete tenant' 
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    
    const confirmMessage = `Are you sure you want to delete ${selectedUsers.size} user(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setIsBulkDeleting(true);
      const userIds = Array.from(selectedUsers);
      
      // Delete users sequentially to avoid overwhelming the server
      const errors: string[] = [];
      for (const userId of userIds) {
        try {
          await api.serverAdmin.deleteUser(userId);
        } catch (error: any) {
          const user = users.find(u => u.id === userId);
          errors.push(user ? `${user.first_name} ${user.last_name}: ${error.response?.data?.detail || 'Failed to delete'}` : `User ${userId}: ${error.response?.data?.detail || 'Failed to delete'}`);
        }
      }
      
      // Remove deleted users from state
      setUsers(prev => prev.filter(user => !selectedUsers.has(user.id)));
      setSelectedUsers(new Set());
      
      // Reload stats
      const statsResponse = await api.serverAdmin.getStats();
      setStats(statsResponse.data);
      
      if (errors.length > 0) {
        setMessage({ 
          type: 'error', 
          text: `Deleted ${userIds.length - errors.length} user(s). Errors: ${errors.join('; ')}` 
        });
      } else {
        setMessage({ type: 'success', text: `Successfully deleted ${userIds.length} user(s)` });
      }
    } catch (error: any) {
      console.error('Failed to bulk delete users:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete users' 
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteTenants = async () => {
    if (selectedTenants.size === 0) return;
    
    const confirmMessage = `Are you sure you want to delete ${selectedTenants.size} tenant(s) and ALL associated data? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setIsBulkDeleting(true);
      const tenantIds = Array.from(selectedTenants);
      
      // Delete tenants sequentially
      const errors: string[] = [];
      for (const tenantId of tenantIds) {
        try {
          await api.serverAdmin.deleteTenant(tenantId);
        } catch (error: any) {
          const tenant = tenants.find(t => t.id === tenantId);
          errors.push(tenant ? `${tenant.name}: ${error.response?.data?.detail || 'Failed to delete'}` : `Tenant ${tenantId}: ${error.response?.data?.detail || 'Failed to delete'}`);
        }
      }
      
      // Remove deleted tenants from state
      setTenants(prev => prev.filter(tenant => !selectedTenants.has(tenant.id)));
      setSelectedTenants(new Set());
      
      // Reload stats
      const statsResponse = await api.serverAdmin.getStats();
      setStats(statsResponse.data);
      
      if (errors.length > 0) {
        setMessage({ 
          type: 'error', 
          text: `Deleted ${tenantIds.length - errors.length} tenant(s). Errors: ${errors.join('; ')}` 
        });
      } else {
        setMessage({ type: 'success', text: `Successfully deleted ${tenantIds.length} tenant(s)` });
      }
    } catch (error: any) {
      console.error('Failed to bulk delete tenants:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete tenants' 
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tenantId)) {
        newSet.delete(tenantId);
      } else {
        newSet.add(tenantId);
      }
      return newSet;
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleSelectAllTenants = () => {
    if (selectedTenants.size === filteredTenants.length) {
      setSelectedTenants(new Set());
    } else {
      setSelectedTenants(new Set(filteredTenants.map(t => t.id)));
    }
  };

  const handleTenantStatusChange = async (tenantId: string, newStatus: 'active' | 'suspended' | 'cancelled') => {
    try {
      await api.serverAdmin.updateTenantStatus(tenantId, { status: newStatus });
      
      setTenants(prev => prev.map(tenant => 
        tenant.id === tenantId ? { ...tenant, status: newStatus } : tenant
      ));
      
      setMessage({ type: 'success', text: `Tenant status updated to ${newStatus}` });
    } catch (error: any) {
      console.error('Failed to update tenant status:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update tenant status' 
      });
    }
  };

  const loadVerificationBypassSetting = async () => {
    try {
      const response = await api.serverAdmin.getDeepLinkVerificationBypass();
      setVerificationBypassEnabled(response.data.enabled);
    } catch (error) {
      console.warn('Failed to load verification bypass setting:', error);
    }
  };

  const loadCampaignSettingsLockSetting = async () => {
    try {
      const response = await api.serverAdmin.getCampaignSettingsLock();
      setCampaignSettingsLockEnabled(response.data.enabled);
    } catch (error) {
      console.warn('Failed to load campaign settings lock setting:', error);
    }
  };

  const loadRatingGateModeLockSetting = async () => {
    try {
      const response = await api.serverAdmin.getRatingGateModeLock();
      setRatingGateModeLockEnabled(response.data.enabled);
    } catch (error) {
      console.warn('Failed to load rating gate mode lock setting:', error);
    }
  };

  const handleToggleVerificationBypass = async () => {
    try {
      setIsUpdatingBypass(true);
      const newValue = !verificationBypassEnabled;
      await api.serverAdmin.updateDeepLinkVerificationBypass({ enabled: newValue });
      setVerificationBypassEnabled(newValue);
      setMessage({ 
        type: 'success', 
        text: `Verification bypass ${newValue ? 'enabled' : 'disabled'} successfully` 
      });
    } catch (error) {
      console.error('Failed to update verification bypass setting:', error);
      setMessage({ type: 'error', text: 'Failed to update verification bypass setting' });
    } finally {
      setIsUpdatingBypass(false);
    }
  };

  const handleToggleCampaignSettingsLock = async () => {
    try {
      setIsUpdatingCampaignLock(true);
      const newValue = !campaignSettingsLockEnabled;
      await api.serverAdmin.updateCampaignSettingsLock({ enabled: newValue });
      setCampaignSettingsLockEnabled(newValue);
      setMessage({ 
        type: 'success', 
        text: `Campaign settings lock ${newValue ? 'enabled' : 'disabled'} successfully` 
      });
    } catch (error) {
      console.error('Failed to update campaign settings lock setting:', error);
      setMessage({ type: 'error', text: 'Failed to update campaign settings lock setting' });
    } finally {
      setIsUpdatingCampaignLock(false);
    }
  };

  const handleToggleRatingGateModeLock = async () => {
    try {
      setIsUpdatingRatingGateLock(true);
      const newValue = !ratingGateModeLockEnabled;
      await api.serverAdmin.updateRatingGateModeLock({ enabled: newValue });
      setRatingGateModeLockEnabled(newValue);
      setMessage({ 
        type: 'success', 
        text: `Rating gate mode lock ${newValue ? 'enabled' : 'disabled'} successfully` 
      });
    } catch (error) {
      console.error('Failed to update rating gate mode lock setting:', error);
      setMessage({ type: 'error', text: 'Failed to update rating gate mode lock setting' });
    } finally {
      setIsUpdatingRatingGateLock(false);
    }
  };

  const loadEmailDiagnostics = async (tenantId?: string) => {
    try {
      setIsLoadingDiagnostics(true);
      const response = await api.serverAdmin.getEmailDiagnostics(tenantId);
      setEmailDiagnostics(response.data);
    } catch (error) {
      console.error('Failed to load email diagnostics:', error);
      setMessage({ type: 'error', text: 'Failed to load email diagnostics' });
    } finally {
      setIsLoadingDiagnostics(false);
    }
  };

  // Load diagnostics when diagnostics tab is selected
  useEffect(() => {
    if (activeTab === 'diagnostics' && !emailDiagnostics) {
      loadEmailDiagnostics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.domain && tenant.domain.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superuser': return 'bg-purple-100 text-purple-800';
      case 'owner': return 'bg-blue-100 text-blue-800';
      case 'admin': return 'bg-indigo-100 text-indigo-800';
      case 'user': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Server Administration</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage users, tenants, and monitor server statistics
            </p>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-md ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'users', label: 'Users' },
                { id: 'tenants', label: 'Tenants' },
                { id: 'compliance', label: 'Compliance' },
                { id: 'diagnostics', label: 'Diagnostics' },
                { id: 'support-inbox', label: 'Support Inbox' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Server Statistics</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                          <dd className="text-lg font-medium text-gray-900">{stats.total_users}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                          <dd className="text-lg font-medium text-gray-900">{stats.active_users}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Total Tenants</dt>
                          <dd className="text-lg font-medium text-gray-900">{stats.total_tenants}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">Recent Registrations</dt>
                          <dd className="text-lg font-medium text-gray-900">{stats.recent_registrations}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Verification Bypass Toggle */}
                <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Deep Link Verification Bypass
                      </h3>
                      <p className="text-sm text-gray-600">
                        {verificationBypassEnabled 
                          ? "Unverified users can get public review platform URLs. Verification is bypassed."
                          : "Only verified users can get public review platform URLs. Unverified users will get private feedback redirects."
                        }
                      </p>
                    </div>
                    <button
                      onClick={handleToggleVerificationBypass}
                      disabled={isUpdatingBypass}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        verificationBypassEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } ${isUpdatingBypass ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={verificationBypassEnabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          verificationBypassEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Campaign Settings Lock Toggle */}
                <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Campaign Settings Lock
                      </h3>
                      <p className="text-sm text-gray-600">
                        {campaignSettingsLockEnabled 
                          ? "Campaign consent and suppression settings are locked and always enabled. Users cannot change these settings when creating campaigns."
                          : "Users can change campaign consent and suppression settings when creating campaigns."
                        }
                      </p>
                    </div>
                    <button
                      onClick={handleToggleCampaignSettingsLock}
                      disabled={isUpdatingCampaignLock}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        campaignSettingsLockEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } ${isUpdatingCampaignLock ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={campaignSettingsLockEnabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          campaignSettingsLockEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Rating Gate Mode Lock Toggle */}
                <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Rating Gate Mode Lock
                      </h3>
                      <p className="text-sm text-gray-600">
                        {ratingGateModeLockEnabled 
                          ? "Rating gate mode (Standard/Strict) is locked. Users cannot change the mode in their rating gate settings."
                          : "Users can change the rating gate mode (Standard/Strict) in their rating gate settings."
                        }
                      </p>
                    </div>
                    <button
                      onClick={handleToggleRatingGateModeLock}
                      disabled={isUpdatingRatingGateLock}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        ratingGateModeLockEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } ${isUpdatingRatingGateLock ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={ratingGateModeLockEnabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          ratingGateModeLockEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">User Management</h3>
                  <div className="flex-1 max-w-lg ml-4">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Bulk Actions Bar */}
                {selectedUsers.size > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-blue-900">
                        {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setSelectedUsers(new Set())}
                        className="text-sm text-blue-700 hover:text-blue-900"
                      >
                        Clear Selection
                      </button>
                      <button
                        onClick={handleBulkDeleteUsers}
                        disabled={isBulkDeleting}
                        className="text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isBulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.size} User${selectedUsers.size > 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {/* Select All Header */}
                    {filteredUsers.length > 0 && (
                      <li className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                            onChange={handleSelectAllUsers}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            Select All ({filteredUsers.length})
                          </span>
                        </div>
                      </li>
                    )}
                    {filteredUsers.map((user) => (
                      <li key={user.id}>
                        <div className="px-4 py-4 flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={() => handleUserSelect(user.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4 flex-shrink-0"
                            />
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-700">
                                    {user.first_name[0]}{user.last_name[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name} {user.last_name}
                                {user.is_owner_anywhere && (
                                  <span className="ml-2 text-xs text-orange-600 font-semibold" title="This user is an owner of at least one tenant">
                                    (Owner)
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                              <div className="mt-2 space-y-1">
                                <div className="text-xs font-medium text-gray-500 mb-1">Tenant Roles:</div>
                                {user.tenant_roles && user.tenant_roles.length > 0 ? (
                                  user.tenant_roles.map((tr) => (
                                    <div key={tr.tenant_id} className="flex items-center space-x-2">
                                      <span className="text-xs text-gray-600">{tr.tenant_name}:</span>
                                      <select
                                        value={tr.role}
                                        onChange={(e) => handleTenantRoleChange(user.id, tr.tenant_id, e.target.value as any)}
                                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                                        title={`Change role in ${tr.tenant_name}`}
                                      >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                        <option value="owner">Owner</option>
                                        <option value="superuser">Superuser</option>
                                      </select>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-xs text-gray-400 italic">No tenant assigned</div>
                                )}
                                <button
                                  onClick={() => handleAssignTenantClick(user)}
                                  className="text-xs text-blue-600 hover:text-blue-800 mt-1 underline"
                                  title="Assign user to a tenant"
                                >
                                  + Assign Tenant
                                </button>
                              </div>
                            </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                              {user.role}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                              {user.status}
                            </span>
                            <div className="flex space-x-2">
                              <select
                                value={user.status}
                                onChange={(e) => handleUserStatusChange(user.id, e.target.value as any)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="suspended">Suspended</option>
                              </select>
                              <select
                                value={user.role}
                                onChange={(e) => handleUserRoleChange(user.id, e.target.value as any)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="owner">Owner</option>
                                <option value="superuser">Superuser</option>
                              </select>
                              <button
                                onClick={() => setDeleteConfirm({ type: 'user', id: user.id, name: `${user.first_name} ${user.last_name} (${user.email})` })}
                                className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                                title="Delete user"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Tenants Tab */}
            {activeTab === 'tenants' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Tenant Management</h3>
                  <div className="flex-1 max-w-lg ml-4">
                    <input
                      type="text"
                      placeholder="Search tenants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Bulk Actions Bar */}
                {selectedTenants.size > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-blue-900">
                        {selectedTenants.size} tenant{selectedTenants.size > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setSelectedTenants(new Set())}
                        className="text-sm text-blue-700 hover:text-blue-900"
                      >
                        Clear Selection
                      </button>
                      <button
                        onClick={handleBulkDeleteTenants}
                        disabled={isBulkDeleting}
                        className="text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isBulkDeleting ? 'Deleting...' : `Delete ${selectedTenants.size} Tenant${selectedTenants.size > 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {/* Select All Header */}
                    {filteredTenants.length > 0 && (
                      <li className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedTenants.size === filteredTenants.length && filteredTenants.length > 0}
                            onChange={handleSelectAllTenants}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            Select All ({filteredTenants.length})
                          </span>
                        </div>
                      </li>
                    )}
                    {filteredTenants.map((tenant) => (
                      <li key={tenant.id}>
                        <div className="px-4 py-4 flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <input
                              type="checkbox"
                              checked={selectedTenants.has(tenant.id)}
                              onChange={() => handleTenantSelect(tenant.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4 flex-shrink-0"
                            />
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-700">
                                    {tenant.name[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {tenant.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {tenant.domain || 'No domain set'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(tenant.status)}`}>
                              {tenant.status}
                            </span>
                            <select
                              value={tenant.status}
                              onChange={(e) => handleTenantStatusChange(tenant.id, e.target.value as any)}
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="active">Active</option>
                              <option value="suspended">Suspended</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            <button
                              onClick={() => setDeleteConfirm({ type: 'tenant', id: tenant.id, name: tenant.name })}
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                              title="Delete tenant and all associated data"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Compliance Tab */}
            {activeTab === 'compliance' && (
              <ComplianceManagement />
            )}

            {/* Diagnostics Tab */}
            {activeTab === 'diagnostics' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Email Diagnostics</h3>
                  <div className="flex items-center space-x-4">
                    <select
                      value={selectedTenantForDiagnostics}
                      onChange={(e) => {
                        setSelectedTenantForDiagnostics(e.target.value);
                        loadEmailDiagnostics(e.target.value || undefined);
                      }}
                      className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Tenants (Server Level)</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => loadEmailDiagnostics(selectedTenantForDiagnostics || undefined)}
                      disabled={isLoadingDiagnostics}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingDiagnostics ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {isLoadingDiagnostics ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : emailDiagnostics ? (
                  <div className="space-y-6">
                    {/* Overall Status */}
                    <div className={`p-4 rounded-lg border-2 ${
                      emailDiagnostics.status === 'healthy' 
                        ? 'bg-green-50 border-green-200' 
                        : emailDiagnostics.status === 'critical'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          emailDiagnostics.status === 'healthy' 
                            ? 'bg-green-500' 
                            : emailDiagnostics.status === 'critical'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                        }`}></div>
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Overall Status: {emailDiagnostics.status.toUpperCase()}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Last checked: {new Date(emailDiagnostics.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Server Level Configuration */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Server-Level Email Configuration</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Postmark Server Token</label>
                          <div className="mt-1 flex items-center">
                            {emailDiagnostics.server_level?.postmark_server_token_configured ? (
                              <>
                                <span className="text-green-600 text-sm">✓ Configured</span>
                                <span className="ml-2 text-xs text-gray-500">
                                  ({emailDiagnostics.server_level.postmark_server_token_length} chars)
                                </span>
                              </>
                            ) : (
                              <span className="text-red-600 text-sm">✗ Not Configured</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">From Email</label>
                          <div className="mt-1">
                            {emailDiagnostics.server_level?.from_email_configured ? (
                              <span className="text-green-600 text-sm">✓ {emailDiagnostics.server_level.from_email}</span>
                            ) : (
                              <span className="text-red-600 text-sm">✗ Not Configured</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Postmark From Email</label>
                          <div className="mt-1 text-sm text-gray-700">
                            {emailDiagnostics.server_level?.postmark_from_email || 'Not configured'}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Email Service Status</label>
                          <div className="mt-1">
                            {emailDiagnostics.server_level?.email_service_initialized ? (
                              <span className="text-green-600 text-sm">✓ Initialized</span>
                            ) : (
                              <span className="text-red-600 text-sm">✗ Not Initialized</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Email Service Test Results */}
                      {emailDiagnostics.server_level?.email_service_test && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="text-sm font-medium text-gray-500">Connection Test</label>
                          <div className="mt-2">
                            {emailDiagnostics.server_level.email_service_test.status === 'success' ? (
                              <div className="bg-green-50 p-3 rounded-md">
                                <span className="text-green-800 text-sm">
                                  ✓ {emailDiagnostics.server_level.email_service_test.message || 'Email client initialized successfully'}
                                </span>
                                {emailDiagnostics.server_level.email_service_test.server_name && (
                                  <div className="text-xs text-green-700 mt-1">
                                    Server: {emailDiagnostics.server_level.email_service_test.server_name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="bg-red-50 p-3 rounded-md">
                                <span className="text-red-800 text-sm font-medium">✗ Connection Failed</span>
                                <div className="text-xs text-red-700 mt-1">
                                  {emailDiagnostics.server_level.email_service_test.error || emailDiagnostics.server_level.email_service_test.message}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tenant Level Configuration */}
                    {emailDiagnostics.tenant_level && 
                     !emailDiagnostics.tenant_level.error && 
                     emailDiagnostics.tenant_level.tenant_id && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Tenant-Level Email Configuration</h4>
                        <div className="mb-4">
                          <div className="text-sm text-gray-600">
                            <strong>Tenant:</strong> {emailDiagnostics.tenant_level.tenant_name} ({emailDiagnostics.tenant_level.tenant_id})
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Integration Settings</label>
                            <div className="mt-1">
                              {emailDiagnostics.tenant_level.integration_settings_exist ? (
                                <span className="text-green-600 text-sm">✓ Exists</span>
                              ) : (
                                <span className="text-yellow-600 text-sm">⚠ Not Found</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Email Enabled</label>
                            <div className="mt-1">
                              {emailDiagnostics.tenant_level.email_enabled ? (
                                <span className="text-green-600 text-sm">✓ Enabled</span>
                              ) : (
                                <span className="text-yellow-600 text-sm">⚠ Disabled</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Postmark API Token</label>
                            <div className="mt-1">
                              {emailDiagnostics.tenant_level.postmark_api_token_configured ? (
                                <span className="text-green-600 text-sm">✓ Configured</span>
                              ) : (
                                <span className="text-yellow-600 text-sm">⚠ Not Configured</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Postmark From Email</label>
                            <div className="mt-1 text-sm text-gray-700">
                              {emailDiagnostics.tenant_level.postmark_from_email || 'Not configured'}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Tenant Email Service</label>
                            <div className="mt-1">
                              {emailDiagnostics.tenant_level.tenant_email_service_initialized ? (
                                <span className="text-green-600 text-sm">✓ Initialized</span>
                              ) : (
                                <span className="text-yellow-600 text-sm">⚠ Not Initialized</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Tenant Email Service Test Results */}
                        {emailDiagnostics.tenant_level.tenant_email_service_test && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-sm font-medium text-gray-500">Connection Test</label>
                            <div className="mt-2">
                              {emailDiagnostics.tenant_level.tenant_email_service_test.status === 'success' ? (
                                <div className="bg-green-50 p-3 rounded-md">
                                  <span className="text-green-800 text-sm">
                                    ✓ {emailDiagnostics.tenant_level.tenant_email_service_test.message || 'Tenant email client initialized successfully'}
                                  </span>
                                  {emailDiagnostics.tenant_level.tenant_email_service_test.server_name && (
                                    <div className="text-xs text-green-700 mt-1">
                                      Server: {emailDiagnostics.tenant_level.tenant_email_service_test.server_name}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="bg-yellow-50 p-3 rounded-md">
                                  <span className="text-yellow-800 text-sm font-medium">⚠ Connection Failed</span>
                                  <div className="text-xs text-yellow-700 mt-1">
                                    {emailDiagnostics.tenant_level.tenant_email_service_test.error || emailDiagnostics.tenant_level.tenant_email_service_test.message}
                                  </div>
                                  {emailDiagnostics.tenant_level.tenant_email_service_test.fallback_available && (
                                    <div className="text-xs text-yellow-700 mt-1">
                                      Fallback to server-level email available
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Recommendations */}
                    {emailDiagnostics.recommendations && emailDiagnostics.recommendations.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Recommendations</h4>
                        <div className="space-y-3">
                          {emailDiagnostics.recommendations.map((rec: any, index: number) => (
                            <div
                              key={index}
                              className={`p-4 rounded-md border-l-4 ${
                                rec.level === 'critical'
                                  ? 'bg-red-50 border-red-500'
                                  : rec.level === 'error'
                                  ? 'bg-red-50 border-red-400'
                                  : 'bg-yellow-50 border-yellow-400'
                              }`}
                            >
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  {rec.level === 'critical' || rec.level === 'error' ? (
                                    <span className="text-red-600 font-bold">⚠</span>
                                  ) : (
                                    <span className="text-yellow-600 font-bold">ℹ</span>
                                  )}
                                </div>
                                <div className="ml-3 flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {rec.issue}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-600">
                                    <strong>Impact:</strong> {rec.impact}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-700">
                                    <strong>Fix:</strong> {rec.fix}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No diagnostics data available. Click Refresh to load.
                  </div>
                )}
              </div>
            )}

            {/* Support Inbox Tab */}
            {activeTab === 'support-inbox' && (
              <SupportInboxTab />
            )}
          </div>
        </div>
      </div>

      {/* Assign Tenant Modal */}
      {showAssignTenantModal && userToAssign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assign Tenant to User
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Assign <strong>{userToAssign.first_name} {userToAssign.last_name}</strong> ({userToAssign.email}) to a tenant.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Tenant
                    </label>
                    <select
                      value={selectedTenantForAssignment}
                      onChange={(e) => setSelectedTenantForAssignment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select a tenant --</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name} {tenant.domain ? `(${tenant.domain})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={selectedRoleForAssignment}
                      onChange={(e) => setSelectedRoleForAssignment(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                      <option value="superuser">Superuser</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAssignTenantModal(false);
                      setUserToAssign(null);
                      setSelectedTenantForAssignment('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                    disabled={isAssigning}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignTenant}
                    disabled={isAssigning || !selectedTenantForAssignment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAssigning ? 'Assigning...' : 'Assign Tenant'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.type && deleteConfirm.id && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm Deletion
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  {deleteConfirm.type === 'user' ? (
                    <>
                      Are you sure you want to delete user <strong>{deleteConfirm.name}</strong>?
                      <br /><br />
                      This will permanently delete the user and all associated data including:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>User account and profile</li>
                        <li>All memberships and tenant associations</li>
                        <li>Sessions and authentication tokens</li>
                        <li>API keys</li>
                        <li>Feedback comments</li>
                      </ul>
                      <br />
                      <strong className="text-red-600">This action cannot be undone.</strong>
                    </>
                  ) : (
                    <>
                      Are you sure you want to delete tenant <strong>{deleteConfirm.name}</strong>?
                      <br /><br />
                      This will permanently delete the tenant and ALL associated data including:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>All users and memberships</li>
                        <li>All contacts, campaigns, and sequences</li>
                        <li>All templates and feedback</li>
                        <li>Billing and subscription data</li>
                        <li>Branding and settings</li>
                        <li>Analytics and reports</li>
                        <li>All other tenant data</li>
                      </ul>
                      <br />
                      <strong className="text-red-600">This action cannot be undone.</strong>
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center px-4 py-3 space-x-3">
                <button
                  onClick={() => {
                    if (deleteConfirm.type === 'user' && deleteConfirm.id) {
                      handleDeleteUser(deleteConfirm.id);
                    } else if (deleteConfirm.type === 'tenant' && deleteConfirm.id) {
                      handleDeleteTenant(deleteConfirm.id);
                    }
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteConfirm({ type: null, id: null, name: null })}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
